import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import EmptyState from "@/components/base/EmptyState";
import Modal from "@/components/base/Modal";
import { formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";
import {
  ACTION_LABELS,
  PERMISSION_MODULES,
  countPermissions,
  type PermissionAction,
} from "@/store/permissions/catalog";
import {
  EMPLOYE_STATUT_ICONS,
  EMPLOYE_STATUT_LABELS,
  EMPLOYE_STATUT_TONES,
  EMPLOYE_STATUT_OPTIONS,
  employeInitiales,
  employeNomComplet,
  employeRoleLabel,
  type EmployeStatut,
} from "../types";
import { useEmployes } from "../EmployesProvider";
import { useRoles } from "@/store/permissions/RolesProvider";
import EmployeForm from "../components/EmployeForm";

export default function AgenceEmployeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith("/proprietaire") ? "/proprietaire/employes" : "/agence/employes";
  const { getEmploye, updateStatut, removeEmploye, resendInvitation, setCurrentEmployeId, currentEmployeId, can } = useEmployes();
  const { roles } = useRoles();
  const employe = id ? getEmploye(id) : undefined;
  const roleNom = employe ? roles.find((r) => r.code === employe.role)?.nom ?? employeRoleLabel(employe.role) : "";

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!employe) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to={base}
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux employés
        </Link>
        <EmptyState
          icon="ri-user-unfollow-line"
          title="Employé introuvable"
          description="Cet employé n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to={base}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les employés
            </Link>
          }
        />
      </div>
    );
  }

  const peutModifier = can("employes.modifier");
  const peutSupprimer = can("employes.supprimer");
  const isActiveProfile = currentEmployeId === employe.id;
  const total = countPermissions(employe.permissions);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Rôle", value: roleNom, icon: "ri-briefcase-line" },
    { label: "Email", value: employe.email || "—", icon: "ri-mail-line" },
    { label: "Téléphone", value: employe.telephone || "—", icon: "ri-phone-line" },
    { label: "Ajouté le", value: formatDate(employe.createdAt), icon: "ri-time-line" },
  ];

  const handleDelete = () => {
    setDeleteOpen(false);
    removeEmploye(employe.id);
    navigate(base);
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={base}
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux employés
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-lg font-semibold text-primary-800">
            {employeInitiales(employe)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={EMPLOYE_STATUT_TONES[employe.statut]} icon={EMPLOYE_STATUT_ICONS[employe.statut]}>
                {EMPLOYE_STATUT_LABELS[employe.statut]}
              </Badge>
              {isActiveProfile && (
                <Badge tone="primary" icon="ri-user-follow-line">
                  Profil affiché actuellement
                </Badge>
              )}
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {employeNomComplet(employe)}
            </h2>
            <p className="mt-1 text-sm text-foreground-600">{roleNom}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:shrink-0">
          {peutModifier && employe.statut === "invite" && (
            <button
              type="button"
              onClick={() => resendInvitation(employe.id)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-4 py-2.5 text-sm font-label font-medium text-accent-900 transition-colors hover:bg-accent-100 cursor-pointer"
            >
              <i className="ri-mail-send-line text-base" aria-hidden="true" />
              Renvoyer l'invitation
            </button>
          )}
          <button
            type="button"
            onClick={() => setCurrentEmployeId(isActiveProfile ? null : employe.id)}
            className={cn(
              "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-label font-medium transition-colors cursor-pointer",
              isActiveProfile
                ? "border-foreground-200 bg-background-50 text-foreground-900 hover:bg-background-100"
                : "border-primary-300 bg-primary-50 text-primary-900 hover:bg-primary-100"
            )}
          >
            <i className={cn(isActiveProfile ? "ri-arrow-go-back-line" : "ri-user-follow-line", "text-base")} aria-hidden="true" />
            {isActiveProfile ? "Revenir à l'accès complet" : "Voir l'espace avec ces permissions"}
          </button>
          {peutModifier && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-edit-line text-base" aria-hidden="true" />
              Modifier
            </button>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3.5 md:px-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                <i className="ri-shield-keyhole-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                Permissions par module
              </h3>
            </div>
            <span className="text-xs text-foreground-500">{total} active{total > 1 ? "s" : ""}</span>
          </header>
          <div className="flex flex-col divide-y divide-background-200">
            {PERMISSION_MODULES.map((module) => {
              const granted = module.actions.filter((action) => employe.permissions[module.key]?.[action]);
              return (
                <div key={module.key} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                        granted.length > 0 ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-400"
                      )}
                    >
                      <i className={cn(module.icon, "text-base")} aria-hidden="true" />
                    </span>
                    <span className="truncate text-sm font-label font-medium text-foreground-950">{module.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {granted.length === 0 && <span className="text-xs text-foreground-400">Aucun accès</span>}
                    {granted.map((action) => (
                      <span
                        key={action}
                        className="inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-secondary-100 px-2 py-0.5 text-[11px] font-label font-medium text-secondary-900"
                      >
                        {ACTION_LABELS[action as PermissionAction]}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex flex-col gap-5">
          <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
            <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
                <i className="ri-information-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Informations</h3>
            </header>
            <dl className="grid grid-cols-1 gap-4 p-4 md:p-5">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </section>

          {peutModifier && (
            <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
              <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className="ri-toggle-line text-base" aria-hidden="true" />
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Statut</h3>
              </header>
              <div className="flex flex-col gap-2 p-4 md:p-5">
                {EMPLOYE_STATUT_OPTIONS.map((option) => {
                  const active = employe.statut === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateStatut(employe.id, option.value as EmployeStatut)}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-md border px-3.5 py-2.5 text-sm font-label font-medium transition-colors cursor-pointer",
                        active
                          ? "border-primary-300 bg-primary-50 text-primary-900"
                          : "border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <i className={EMPLOYE_STATUT_ICONS[option.value as EmployeStatut]} aria-hidden="true" />
                        {option.label}
                      </span>
                      {active && <i className="ri-check-line text-base" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {peutSupprimer && (
            <section className="flex flex-col gap-3 rounded-lg border border-primary-200 bg-primary-50/40 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-label font-medium text-primary-900">Retirer cet employé</p>
                  <p className="mt-0.5 text-xs text-foreground-600">La fiche sera supprimée et ses accès révoqués.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
              >
                <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                Supprimer l'employé
              </button>
            </section>
          )}
        </div>
      </div>

      <EmployeForm open={editOpen} onClose={() => setEditOpen(false)} employe={employe} />

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        size="sm"
        title="Supprimer l'employé"
        description={`Voulez-vous vraiment supprimer « ${employeNomComplet(employe)} » ? Cette action est définitive.`}
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setDeleteOpen(false)}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </Modal>
    </div>
  );
}